"use client";

import { useEffect, useState } from "react";
import GetInTouchHeader from "@/components/get-in-touch/GetInTouchHeader";
import SurveyRadioField from "./SurveyRadioField";
import { getSurveyQuestionFlow } from "./_constants/surveyQuestions";
import { getSurveyAnswers, isSurveyCompleted, saveSurveyAnswers } from "@/lib/utils/survey-storage";
import { navigateBack } from "@/components/get-in-touch/enquiryFlowHistory";
import { useSurveySheetStore } from "@/store/surveySheetStore";
import { useEnquiryFlowStore } from "@/store/enquiryFlowStore";
import { inhomzEvents } from "@/app/(fevicol-inhomz)/_lib/events";
import { profileEventProps, surveyEventProps } from "@/app/(fevicol-inhomz)/_lib/profileEventProps";
import { useInhomzTrack } from "@/app/(fevicol-inhomz)/_hooks/useInhomzTrack";
import { useTrackWhenActive } from "@/app/(fevicol-inhomz)/_hooks/useTrackWhenActive";

// The actual project-details survey UI, shared between the standalone
// SurveySheet (its own sidesheet) and EnquiryFlowShell (hosted inline as a
// sliding "page" of the mandatory-form -> review chain). `isActive` tells
// this instance whether IT is the one currently in use (vs. mounted but
// slid out of view, or a dormant standalone instance while the flow variant
// is the active one) — only the active instance reseeds its answers when
// (re)entered.
export default function SurveyBody({
  isActive
}) {
  const {
    initialAnswers,
    onComplete,
    onSkip,
    close,
    context
  } = useSurveySheetStore();
  const [answers, setAnswers] = useState({});
  const digitalProfile = useEnquiryFlowStore(state => state.digitalProfile);
  const track = useInhomzTrack();

  // The mandatory-form -> survey -> review chain needs "Skip"/"Review and
  // send" (there's a next step either way); a standalone open — the
  // listing's/homepage's survey icon, no enquiry in progress — just has one
  // "Save" action that writes to localStorage and closes, no Skip to offer.
  const isStandaloneOpen = context !== "enquiryFlow";
  const baseProps = {
    context: isStandaloneOpen ? "standalone" : "enquiry_flow",
    ...(isStandaloneOpen ? {} : profileEventProps(digitalProfile))
  };
  useTrackWhenActive(isActive, inhomzEvents.projectDetailsForm.VIEWED, baseProps);
  useEffect(() => {
    if (isActive) {
      setAnswers(initialAnswers ?? getSurveyAnswers());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
  const handleSelect = (questionId, option) => {
    const next = {
      ...answers,
      [questionId]: option
    };
    setAnswers(next);
    // Persisted per pick: back may arrive via the browser button, bypassing this component.
    saveSurveyAnswers(next);
  };
  const isComplete = isSurveyCompleted(answers);
  // In the enquiry chain, Review (and the API) never see an incomplete survey.
  const isNextBlocked = !isStandaloneOpen && !isComplete;
  const handleBack = () => {
    track(inhomzEvents.projectDetailsForm.BACK_CLICKED, baseProps);
    navigateBack();
  };
  const handleSkip = () => {
    track(inhomzEvents.projectDetailsForm.SKIP_CLICKED, {
      ...baseProps,
      ...surveyEventProps(answers)
    });
    saveSurveyAnswers(answers);
    const skip = onSkip;
    close();
    skip?.(answers);
  };
  const handleComplete = () => {
    track(isStandaloneOpen ? inhomzEvents.projectDetailsForm.SAVE_CLICKED : inhomzEvents.projectDetailsForm.CONTINUE_CLICKED, {
      ...baseProps,
      ...surveyEventProps(answers)
    });
    saveSurveyAnswers(answers);
    const complete = onComplete;
    close();
    complete?.(answers);
  };
  return <div className="mx-auto flex h-full w-full flex-col bg-[--colorSurfaceSurface]">
      <GetInTouchHeader title="Project details" onBack={handleBack} rightSlot={isStandaloneOpen || !isComplete ? null : <button type="button" onClick={handleSkip} className="m3-label-large flex h-12 items-center px-3 uppercase text-[--colorPrimaryPrimary]">
              Skip
            </button>} />

      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        <p className="m3-body-medium text-[--textSecondarySecondary]">
          A few details will help Interior Experts understand your project before you talk.
        </p>

        <div className="divide-y divide-[--colorSecondarySecondaryContainer]">
          {getSurveyQuestionFlow(answers).map(q => <SurveyRadioField key={q.id} icon={q.icon} question={q.optional ? `${q.question} (optional)` : q.question} options={q.options} selected={answers[q.id]} onSelect={option => handleSelect(q.id, option)} />)}
        </div>
      </div>

      <div className="sticky bottom-0 flex items-center justify-end border-t border-[--colorSecondarySecondaryContainer] bg-[--colorSurfaceSurface] px-6 py-4">
        <button type="button" onClick={handleComplete} disabled={isNextBlocked} className="m3-label-large flex h-10 items-center rounded-[--radius100] bg-[--colorPrimaryPrimary] px-6 text-[--textPrimaryOnColorPrimary] disabled:opacity-40">
          {isStandaloneOpen ? "Save" : "Review and send"}
        </button>
      </div>
    </div>;
}